import * as pdfjsLib from 'pdfjs-dist';

export interface StatuteSection {
  id: string;
  label: string;
  title: string;
  content: string;
}

export async function extractAndFormatPDF(pdfFile: File, onProgress: (progress: number) => void): Promise<string> {
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let rawText = '';
  
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    
    let lastY = -1;
    let pageText = "";
    
    // Sort items visually by Y (descending) and then X (ascending)
    const items = textContent.items.map((item: any) => ({
      str: item.str,
      x: item.transform[4],
      y: item.transform[5],
      height: item.height || 10
    }));

    items.sort((a, b) => {
      if (Math.abs(a.y - b.y) > 5) {
        return b.y - a.y; 
      }
      return a.x - b.x;
    });

    for (const item of items) {
      if (lastY !== -1 && Math.abs(lastY - item.y) > item.height * 1.2) {
        pageText += '\n';
      } else if (pageText.length > 0 && !pageText.endsWith(' ') && !pageText.endsWith('\n')) {
        pageText += ' ';
      }
      pageText += item.str.trim();
      lastY = item.y;
    }
    
    rawText += pageText + '\n\n';
    onProgress(Math.floor((i / pdf.numPages) * 50));
  }
  
  let text = rawText.replace(/\r\n/g, '\n');
  text = text.replace(/^\s*\d+\s*$/gm, ''); // remove page numbers

  const sections: StatuteSection[] = [];
  let currentSection: StatuteSection | null = null;
  let preamble = "";

  const paragraphs = text.split(/\n\s*\n/);
  
  // Heuristic: looks for "1. Title of section.—Content"
  // Or "Section 1. Title"
  const sectionRegex1 = /^(\d{1,4}[A-Z]?)\.\s+([^—\.-]+)[—\.-]+(.*)$/is;
  const sectionRegex2 = /^(?:Section|Article|Rule|Order)\s+(\d{1,4}[A-Z]?)\.?\s*(.*)$/is;

  for (const para of paragraphs) {
    const trimmed = para.trim().replace(/\s+/g, ' ');
    if (!trimmed) continue;

    let isHeader = false;
    let secNum = "";
    let secTitle = "";
    let secContent = "";

    const match1 = trimmed.match(sectionRegex1);
    if (match1 && match1[2].length < 150) {
      isHeader = true;
      secNum = match1[1];
      secTitle = match1[2].trim();
      secContent = match1[3].trim();
    } else {
      const match2 = trimmed.match(sectionRegex2);
      if (match2 && match2[2].length < 150) {
        isHeader = true;
        secNum = match2[1];
        secTitle = match2[2].trim();
        secContent = "";
      }
    }

    if (isHeader) {
      if (currentSection) {
        sections.push(currentSection);
      }
      currentSection = {
        id: `section-${secNum.replace(/[^A-Za-z0-9]/g, '')}`,
        label: `SECTION ${secNum}`,
        title: secTitle,
        content: secContent
      };
    } else {
      if (currentSection) {
        currentSection.content += (currentSection.content ? '\n\n' : '') + trimmed;
      } else {
        preamble += (preamble ? '\n\n' : '') + trimmed;
      }
    }
  }

  if (currentSection) {
    sections.push(currentSection);
  }

  if (sections.length === 0) {
      return JSON.stringify([{
          id: 'full-document',
          label: 'DOCUMENT',
          title: 'Full Text',
          content: text
      }]);
  }

  if (preamble.length > 20) {
      sections.unshift({
          id: 'preamble',
          label: 'PREAMBLE',
          title: 'Introduction',
          content: preamble.trim()
      });
  }

  return JSON.stringify(sections);
}

